import type { HighFrequencyGameId } from '@lottocom/high-frequency-game-mapping'

import { type LicensedTerritory, lotteryIdsByLicensedTerritory } from '@lottocom/lottery-mapping'

import type { LineItemField } from '~/components/LineItem/LineItem.vue'

import {
  type HighFrequency_GameId,
  LotteryId,
  type Numbers,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

export const filterIncorrectLotteryIds = (
  licensedTerritory: string,
  lotteryIds?: unknown,
): LotteryId[] => {
  if (!lotteryIds) return []

  const availableLotteries = lotteryIdsByLicensedTerritory[licensedTerritory as LicensedTerritory]
  const normalizedLotteries = Array.isArray(lotteryIds) ? lotteryIds : [lotteryIds]

  return normalizedLotteries
    .map(getLotteryIdInCorrectCase)
    .filter(isTruthy)
    .filter((lottery) => availableLotteries.includes(lottery))
}

export const getLotteryCssVarConstructor = (
  licensedTerritory: string,
  lotteryOrGameId: HighFrequency_GameId | HighFrequencyGameId | LotteryId,
) => {
  const formattedLicensedTerritory = licensedTerritory.toLowerCase()

  const createCssVar = (suffix: string, fallback = 'unset') => {
    const formattedLotteryId = lotteryOrGameId.toLowerCase()

    const lotteryAndLicensedTerritorySpecific = `--lottery-${formattedLotteryId}-${formattedLicensedTerritory}-${suffix}`
    const lotterySpecific = `--lottery-${formattedLotteryId}-${suffix}`
    return `var(${lotteryAndLicensedTerritorySpecific}, var(${lotterySpecific}, ${fallback}))`
  }

  return { createCssVar }
}

export const getLotteryNumberFields = (numbers: Numbers[]) =>
  numbers.map<LineItemField>(({ values, ...number }) => ({
    ...number,
    numbers: values.map((value) => ({ value })),
  }))

const DEVICE_WIDTH_THRESHOLD = 600

/**
 * Do not return `1` for one line, instead use `undefined`.
 * Otherwise the selected numbers on the options page will have visual bugs.
 */
export const getCountOfFieldRows = (totalFields: number, lottery: LotteryId, width: number) => {
  switch (lottery) {
    case LotteryId.MegaMillions:
    case LotteryId.PowerBall:
      // ignore extraHit
      return undefined
    case LotteryId.NewYorkPick10:
      // Results have 20 numbers
      // Orders have 10 numbers
      if (width > DEVICE_WIDTH_THRESHOLD) return totalFields === 10 ? undefined : 2

      return totalFields === 10 ? 2 : 4
    case LotteryId.TexasAllOrNothing:
      return width > DEVICE_WIDTH_THRESHOLD ? undefined : 2
    default:
      if (width > DEVICE_WIDTH_THRESHOLD) return undefined
      else if (totalFields > 18) return 4
      else if (totalFields > 12) return 3
      else if (totalFields > 7) return 2

      return undefined
  }
}
